import { useId, useState } from 'react';

function readOpen(key: string, fallback: boolean): boolean {
  try {
    const stored = localStorage.getItem(key);
    return stored === null ? fallback : stored === '1';
  } catch {
    return fallback;
  }
}

function writeOpen(key: string, open: boolean): void {
  try {
    localStorage.setItem(key, open ? '1' : '0');
  } catch {
    // Storage blocked: it just will not be remembered.
  }
}

/**
 * A section that tucks away behind its header.
 *
 * Long pages — the wardrobe, a lobby's settings, the front page — read as a list
 * of headers, each saying what is inside ("Frames · 12/40 · Neon Ring"), and open
 * only what you are after. Whether each one was left open is remembered on this
 * phone, by `id`.
 */
export function Fold({
  id,
  label,
  summary,
  defaultOpen = false,
  children,
}: {
  /** Unique across the app, so each fold remembers its own state. */
  id: string;
  label: React.ReactNode;
  /** A short line after the label: a count, the current pick. */
  summary?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const key = `ofc:fold:${id}`;
  const [open, setOpen] = useState(() => readOpen(key, defaultOpen));
  const body = useId();

  const toggle = () => {
    setOpen((was) => {
      writeOpen(key, !was);
      return !was;
    });
  };

  return (
    <section className="fold">
      <button type="button" className="fold-head" aria-expanded={open} aria-controls={body} onClick={toggle}>
        <span className="sign small" style={{ flexShrink: 0 }}>
          {label}
        </span>
        <span className="grow small muted truncate">{summary}</span>
        <span aria-hidden className="fold-chevron">
          ▸
        </span>
      </button>
      {open && (
        <div id={body} className="fold-body col" style={{ gap: 8 }}>
          {children}
        </div>
      )}
    </section>
  );
}
